import { useEffect, useState } from "react";
import { VerifiedBadge } from "@/components/market/AndroidVerification";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { PageHero } from "@/components/layout/PageHero";
import { AppGrid } from "@/components/apps/AppGrid";
import { WarningBlock } from "@/components/market/WarningBlock";
import { DeveloperDirectory } from "@/components/market/DeveloperDirectory";
import NotFound from "@/pages/NotFound";
import { CategoryChips, byCategory } from "@/components/apps/CategoryChips";
import { PlatformChips, byPlatform } from "@/components/apps/Platform";
import { CustomPlanCta } from "@/components/apps/PlanNotes";

// "Applications d'autres développeurs" (no slug) or one developer's public page (with slug).
export default function ThirdParty() {
  const { slug } = useParams();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [off, setOff] = useState(false);
  const [cat, setCat] = useState("all");
  const [plat, setPlat] = useState("all");

  useEffect(() => {
    setData(null);
    setOff(false);
    setCat("all");
    setPlat("all");
    const req = slug
      ? api.get(`/creators/developers/${slug}`)
      : Promise.all([api.get("/creators/apps"), api.get("/creators/developers")]).then(([a, d]) => ({ data: { apps: a.data, devs: d.data } }));
    req.then(({ data: d }) => setData(d)).catch((err) => (err?.response?.status === 404 ? setOff(true) : setData({ apps: [], error: true })));
  }, [slug]);

  if (off) return <NotFound gone />;
  return (
    <div data-testid={slug ? "developer-public-page" : "third-party-page"}>
      <PageHero eyebrow={t(slug ? "market.devEyebrow" : "market.eyebrow")} title={slug ? data?.brand || "…" : t("market.title")}>
        {slug && data?.android_verified && <div className="mt-3"><VerifiedBadge testId="developer-verified-badge" /></div>}
        {slug ? (
          <Link to="/developpeurs" className="mt-4 inline-flex items-center gap-2 text-sm text-[var(--gold)] hover:text-[var(--gold-bright)]" data-testid="developer-back-link">
            <ArrowLeft className="h-4 w-4 rtl:-scale-x-100" /> {t("market.back")}
          </Link>
        ) : (
          <p className="mt-4 max-w-2xl text-sm text-neutral-400">
            {t("market.intro")}{" "}
            <Link to="/apps" className="text-[var(--gold)] hover:text-[var(--gold-bright)]" data-testid="third-party-official-link">{t("market.officialLink")}</Link>
          </p>
        )}
      </PageHero>
      <section className="mx-auto max-w-7xl space-y-8 px-4 sm:px-8">
        <CustomPlanCta slim testId={slug ? "developer-custom-plan" : "third-party-custom-plan"} />
        <WarningBlock testId={slug ? "developer-page-warning" : "third-party-warning"} />
        {!slug && <DeveloperDirectory devs={data?.devs} />}
        <div>
          <h2 className="mb-4 font-display text-sm font-bold uppercase tracking-wider text-[var(--gold-bright)] md:text-base" data-testid="third-party-apps-title">
            {t(slug ? "market.theirApps" : "market.allApps")}
          </h2>
          <CategoryChips apps={data?.apps} value={cat} onChange={setCat} prefix={slug ? "developer" : "third-party"} />
          <PlatformChips apps={data?.apps} value={plat} onChange={setPlat} prefix={slug ? "developer" : "third-party"} />
          <AppGrid apps={data ? byPlatform(byCategory(data.apps, cat), plat) : null} error={data?.error} />
        </div>
      </section>
    </div>
  );
}
